import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";

// Asynchronously load font CSS files — they are not needed for
// the initial paint, and loading them sync blocks rendering.
// The inlined critical CSS includes fallback font-family declarations
// with font-display: swap (see index.css).
Promise.all([
  import("@fontsource/poppins/latin-400.css"),
  import("@fontsource/poppins/latin-700.css"),
  import("@fontsource/poppins/latin-800.css"),
]).catch(() => {
  // Fonts are non-critical; failures degrade gracefully.
  console.warn("Font CSS failed to load");
});
import App from "./App.jsx";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./contexts/AuthContext";
import { HelmetProvider } from "react-helmet-async";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Reduce aggressive refetching
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      refetchOnMount: true,
      retry: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      networkMode: "offlineFirst",

      // Add error handling
      onError: (error) => {
        console.error("Query error:", error);
      },
    },
    mutations: {
      networkMode: "always",
    },
  },
});

createRoot(document.getElementById("root")).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </QueryClientProvider>
  </StrictMode>
);
